import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { Badge, Modal } from '../Scheduler/utils';
import { getWifiPosterTemplateById, WIFI_POSTER_TEMPLATES } from './wifiPosterTemplates';
import './WifiTab.css';

const escapeWifiValue = (value = '') =>
  String(value).replace(/[\\\\;,:"]/g, (match) => `\\${match}`);

const buildWifiPayload = (ssid, password) => {
  const safeSsid = escapeWifiValue(ssid);
  const safePassword = escapeWifiValue(password);
  if (!safeSsid) return '';
  if (!safePassword) return `WIFI:T:nopass;S:${safeSsid};;`;
  return `WIFI:T:WPA;S:${safeSsid};P:${safePassword};;`;
};

const normalizeUrl = (raw) => {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value.replace(/^\/+/, '')}`;
};

const shortUrl = (raw) => {
  const value = String(raw || '').trim();
  if (!value) return '';
  return value.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
};

function WifiTab({ siteId, site, selectedDevice, onGoToSiteDetails }) {
  const token = localStorage.getItem('token');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [initialWifi, setInitialWifi] = useState({ ssid: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingOverlaySettings, setSavingOverlaySettings] = useState(false);
  const [error, setError] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [siteDetails, setSiteDetails] = useState(site || null);
  const [selectedAdId, setSelectedAdId] = useState('connect-wifi');
  const [selectedTemplateId, setSelectedTemplateId] = useState(WIFI_POSTER_TEMPLATES[0].id);
  const [printOpen, setPrintOpen] = useState(false);
  const [defaultAdOverlays, setDefaultAdOverlays] = useState([]);
  const [templateCopy, setTemplateCopy] = useState({ kicker: '', title: '', sub: '' });
  const [visibleFields, setVisibleFields] = useState({
    venue: true,
    address: true,
    website: true,
    link: true,
    details: true,
  });
  const printCleanupRef = useRef(() => {});
  const settingsDebounceRef = useRef(null);
  const hydratingSettingsRef = useRef(false);

  const selectedTemplate = useMemo(
    () => getWifiPosterTemplateById(selectedTemplateId),
    [selectedTemplateId]
  );

  const dirty = useMemo(() => {
    return (
      (wifiSsid || '') !== (initialWifi.ssid || '') ||
      (wifiPassword || '') !== (initialWifi.password || '')
    );
  }, [wifiPassword, wifiSsid, initialWifi]);

  const venue = useMemo(() => {
    const source = siteDetails || site || null;
    return {
      name: source?.name || '',
      address: source?.address || '',
      website: source?.website || '',
      logoUrl: source?.logo_url || source?.logoUrl || '',
      facebookUrl: source?.facebook_url || source?.facebookUrl || '',
      instagramUrl: source?.instagram_url || source?.instagramUrl || '',
      tripadvisorUrl: source?.tripadvisor_url || source?.tripadvisorUrl || '',
      googleMapsUrl: source?.google_url || source?.googleMapsUrl || '',
      googleReviewUrl: source?.google_review_url || source?.googleReviewUrl || '',
      menuUrl: source?.menu_url || source?.menuUrl || '',
    };
  }, [siteDetails, site]);

  const defaultAds = useMemo(() => ([
    {
      id: 'connect-wifi',
      defaultKey: 'wifi',
      label: 'Connect to Wifi',
      type: 'wifi',
      requiredLabel: 'Wi-Fi SSID',
      copy: {
        kicker: '✨ Connect to WiFi',
        title: 'Scan & you’re in',
        sub: 'Fast guest access — no typing.',
        finePrint: 'If prompted, allow “Join Network”.',
        qrAlt: 'WiFi QR code',
        qrPlaceholder: 'Enter an SSID to generate a QR code.',
        ctaTitle: 'Need help?',
        ctaSub: 'Ask a member of staff.',
      },
      steps: { actionLabel: 'Join WiFi' },
      getQrValue: () => buildWifiPayload(wifiSsid.trim(), wifiPassword.trim()),
      getDetails: () => ([
        { label: 'Network', value: wifiSsid.trim() },
        { label: 'Password', value: wifiPassword.trim() ? wifiPassword.trim() : 'No password' },
      ]),
      enabled: () => Boolean(wifiSsid.trim()),
    },
    {
      id: 'facebook-like',
      defaultKey: 'facebook',
      label: 'Like us on Facebook',
      type: 'url',
      field: 'facebookUrl',
      requiredLabel: 'Facebook Page URL',
      copy: {
        kicker: '👍 Like us on Facebook',
        title: 'Scan to open our page',
        sub: 'Tap Like to stay updated.',
        qrAlt: 'Facebook QR code',
        qrPlaceholder: 'Add a Facebook Page URL in Site Details to enable.',
      },
      steps: { actionLabel: 'Open' },
    },
    {
      id: 'instagram-follow',
      defaultKey: 'instagram',
      label: 'Follow us on Instagram',
      type: 'url',
      field: 'instagramUrl',
      requiredLabel: 'Instagram URL',
      copy: {
        kicker: '📸 Follow us on Instagram',
        title: 'Scan to follow',
        sub: 'Tag us in your pics.',
        qrAlt: 'Instagram QR code',
        qrPlaceholder: 'Add an Instagram URL in Site Details to enable.',
      },
      steps: { actionLabel: 'Open' },
    },
    {
      id: 'google-maps',
      defaultKey: 'google_maps',
      label: 'Find us on Google Maps',
      type: 'url',
      field: 'googleMapsUrl',
      requiredLabel: 'Google Maps URL',
      copy: {
        kicker: '📍 Find us on Google Maps',
        title: 'Scan for directions',
        sub: 'Open in Google Maps.',
        qrAlt: 'Google Maps QR code',
        qrPlaceholder: 'Add a Google Maps URL in Site Details to enable.',
      },
      steps: { actionLabel: 'Open' },
    },
    {
      id: 'tripadvisor-review',
      defaultKey: 'tripadvisor',
      label: 'Tripadvisor Review',
      type: 'url',
      field: 'tripadvisorUrl',
      requiredLabel: 'TripAdvisor URL',
      copy: {
        kicker: '⭐ Tripadvisor',
        title: 'Leave us a review',
        sub: 'Tell us what you loved.',
        qrAlt: 'Tripadvisor QR code',
        qrPlaceholder: 'Add a TripAdvisor URL in Site Details to enable.',
      },
      steps: { actionLabel: 'Review' },
    },
    {
      id: 'google-review',
      defaultKey: 'google_review',
      label: 'Google Review',
      type: 'url',
      field: 'googleReviewUrl',
      requiredLabel: 'Google Review URL',
      copy: {
        kicker: '⭐ Google Reviews',
        title: 'Leave us a review',
        sub: 'Help others find us.',
        qrAlt: 'Google review QR code',
        qrPlaceholder: 'Add a Google Review URL in Site Details to enable.',
      },
      steps: { actionLabel: 'Review' },
    },
    {
      id: 'visit-website',
      defaultKey: 'website',
      label: 'Visit Website',
      type: 'url',
      field: 'website',
      requiredLabel: 'Website',
      copy: {
        kicker: '🌐 Visit our website',
        title: 'Scan to view',
        sub: 'Menus, events, and more.',
        qrAlt: 'Website QR code',
        qrPlaceholder: 'Add a Website in Site Details to enable.',
      },
      steps: { actionLabel: 'Open' },
    },
    {
      id: 'view-menu',
      defaultKey: 'menu',
      label: 'View Menu',
      type: 'url',
      field: 'menuUrl',
      requiredLabel: 'Menu URL',
      copy: {
        kicker: '🍽️ View the menu',
        title: 'Scan to open',
        sub: 'Allergens and specials.',
        qrAlt: 'Menu QR code',
        qrPlaceholder: 'Add a Menu URL in Site Details to enable.',
      },
      steps: { actionLabel: 'Open' },
    },
  ]), [wifiPassword, wifiSsid]);

  const selectedAd = useMemo(
    () => defaultAds.find((a) => a.id === selectedAdId) || defaultAds[0],
    [defaultAds, selectedAdId]
  );

  const effectiveCopy = useMemo(() => {
    const base = selectedAd?.copy || {};
    const kicker = String(templateCopy.kicker || '').trim() ? templateCopy.kicker : base.kicker;
    const title = String(templateCopy.title || '').trim() ? templateCopy.title : base.title;
    const sub = String(templateCopy.sub || '').trim() ? templateCopy.sub : base.sub;
    return { ...base, kicker, title, sub };
  }, [selectedAd, templateCopy]);

  const selectedFullOverlay = useMemo(() => {
    const key = selectedAd?.defaultKey || '';
    if (!key) return null;
    return (defaultAdOverlays || []).find(
      (o) => o.default_ad_key === key && String(o.type || '').toLowerCase() === 'full'
    );
  }, [defaultAdOverlays, selectedAd]);

  const missingUrlMessage = useMemo(() => {
    if (selectedAd.type !== 'url') return '';
    const value = venue[selectedAd.field] || '';
    if (String(value || '').trim()) return '';
    return `Please paste the correct url into the ${selectedAd.requiredLabel} field in Settings / Site Details to enable ${selectedAd.label}.`;
  }, [selectedAd, venue]);

  const urlMissing = useMemo(() => {
    if (selectedAd.type !== 'url') return false;
    const value = venue[selectedAd.field] || '';
    return !String(value || '').trim();
  }, [selectedAd, venue]);

  useEffect(() => {
    if (urlMissing) setPrintOpen(false);
  }, [urlMissing]);

  const qrValue = useMemo(() => {
    if (selectedAd.type === 'wifi') return selectedAd.getQrValue();
    const raw = venue[selectedAd.field] || '';
    return normalizeUrl(raw);
  }, [selectedAd, venue]);

  const enabled = useMemo(() => {
    if (selectedAd.type === 'wifi') return selectedAd.enabled();
    return Boolean(qrValue);
  }, [qrValue, selectedAd]);

  const details = useMemo(() => {
    if (selectedAd.type === 'wifi') return selectedAd.getDetails();
    const raw = venue[selectedAd.field] || '';
    if (!String(raw || '').trim()) return [];
    return [
      { label: 'Open', value: selectedAd.label },
      { label: 'URL', value: shortUrl(raw) },
    ];
  }, [selectedAd, venue]);

  useEffect(() => {
    setSiteDetails(site || null);
  }, [site]);

  useEffect(() => {
    if (!siteId) return;
    const storedSubTab = localStorage.getItem(`defaultAdsSubTab:${siteId}`);
    if (storedSubTab && defaultAds.some((t) => t.id === storedSubTab)) {
      setSelectedAdId(storedSubTab);
    } else {
      setSelectedAdId(defaultAds[0].id);
    }
  }, [siteId]);

  useEffect(() => {
    if (!siteId) return;
    localStorage.setItem(`defaultAdsSubTab:${siteId}`, selectedAdId);
  }, [selectedAdId, siteId]);

  useEffect(() => {
    if (!siteId || !token) return;
    let cancelled = false;
    const fetchOverlays = async () => {
      try {
        const res = await axios.get(`/api/default-ads/overlays?siteId=${siteId}&activeOnly=0`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setDefaultAdOverlays(res.data?.overlays || []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load default ad overlays', err);
        setDefaultAdOverlays([]);
      }
    };
    fetchOverlays();
    return () => {
      cancelled = true;
    };
  }, [siteId, token]);

  useEffect(() => {
    if (!selectedFullOverlay) {
      hydratingSettingsRef.current = true;
      setSelectedTemplateId(WIFI_POSTER_TEMPLATES[0].id);
      setTemplateCopy({ kicker: '', title: '', sub: '' });
      setVisibleFields({
        venue: true,
        address: true,
        website: true,
        link: true,
        details: true,
      });
      window.setTimeout(() => {
        hydratingSettingsRef.current = false;
      }, 0);
      return;
    }

    hydratingSettingsRef.current = true;
    const overlayTemplate = selectedFullOverlay.template_key;
    if (overlayTemplate && WIFI_POSTER_TEMPLATES.some((t) => t.id === overlayTemplate)) {
      setSelectedTemplateId(overlayTemplate);
    } else {
      setSelectedTemplateId(WIFI_POSTER_TEMPLATES[0].id);
    }

    const cfg = selectedFullOverlay.template_config || {};
    setTemplateCopy({
      kicker: String(cfg.kicker || ''),
      title: String(cfg.title || ''),
      sub: String(cfg.sub || ''),
    });

    const vis = selectedFullOverlay.visible_fields || {};
    setVisibleFields({
      venue: vis.venue !== false,
      address: vis.address === true,
      website: vis.website === true,
      link: vis.link !== false,
      details: vis.details !== false,
    });

    window.setTimeout(() => {
      hydratingSettingsRef.current = false;
    }, 0);
  }, [selectedFullOverlay]);

  useEffect(() => {
    const fetchWifi = async () => {
      if (!siteId || !token) return;
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`/api/sites/${siteId}/wifi`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ssid = res.data?.wifi_ssid || '';
        const password = res.data?.wifi_password || '';
        setWifiSsid(ssid);
        setWifiPassword(password);
        setInitialWifi({ ssid, password });
      } catch (err) {
        console.error('Error fetching wifi settings', err);
        setError('Failed to load wifi settings.');
      } finally {
        setLoading(false);
      }
    };

    fetchWifi();
  }, [siteId, token]);

  useEffect(() => {
    let cancelled = false;
    const generate = async () => {
      if (!qrValue) {
        setQrDataUrl('');
        return;
      }
      try {
        const dataUrl = await QRCode.toDataURL(qrValue, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 768,
        });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (err) {
        console.error('Error generating QR', err);
        if (!cancelled) setQrDataUrl('');
      }
    };
    generate();
    return () => {
      cancelled = true;
    };
  }, [qrValue]);

  const saveOverlaySettings = async ({ silent = true } = {}) => {
    if (!selectedFullOverlay?.id || !siteId || !token) return false;
    const debugDefaultAds = localStorage.getItem('debugDefaultAds') === '1';
    try {
      setSavingOverlaySettings(true);
      if (debugDefaultAds) {
        console.log('[DefaultAdsUI] Saving overlay settings', {
          overlayId: selectedFullOverlay.id,
          template_key: selectedTemplateId,
          template_config: {
            kicker: String(effectiveCopy.kicker || '').trim(),
            title: String(effectiveCopy.title || '').trim(),
            sub: String(effectiveCopy.sub || '').trim(),
          },
          visible_fields: visibleFields,
        });
      }
      await axios.put(
        `/api/default-ads/overlays/${selectedFullOverlay.id}/settings`,
        {
          template_key: selectedTemplateId,
          template_config: {
            kicker: String(effectiveCopy.kicker || '').trim(),
            title: String(effectiveCopy.title || '').trim(),
            sub: String(effectiveCopy.sub || '').trim(),
          },
          visible_fields: visibleFields,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (debugDefaultAds) {
        console.log('[DefaultAdsUI] Saved overlay settings', {
          overlayId: selectedFullOverlay.id,
          template_key: selectedTemplateId,
        });
      }
      return true;
    } catch (err) {
      console.error('Failed to save default ad settings', err);
      const status = err?.response?.status || null;
      if (status === 409) {
        const message =
          err?.response?.data?.error ||
          'Database migration required to save template settings. Apply docs/DB_DEFAULT_AD_TEMPLATE_SETTINGS.md and restart containers.';
        setError(message);
        if (!silent) alert(message);
        return false;
      }
      if (!silent) {
        alert('Failed to save default ad settings.');
      }
      return false;
    } finally {
      setSavingOverlaySettings(false);
    }
  };

  useEffect(() => {
    if (!selectedFullOverlay?.id || !siteId || !token) return;
    if (hydratingSettingsRef.current) return;
    if (settingsDebounceRef.current) window.clearTimeout(settingsDebounceRef.current);
    settingsDebounceRef.current = window.setTimeout(() => {
      saveOverlaySettings({ silent: true });
    }, 450);
    return () => {
      if (settingsDebounceRef.current) window.clearTimeout(settingsDebounceRef.current);
    };
  }, [selectedFullOverlay?.id, selectedTemplateId, templateCopy, visibleFields, siteId, token]);

  const previewOnTv = async () => {
    if (!selectedFullOverlay?.id) {
      alert('Default ad overlay is not available yet.');
      return;
    }
    if (!selectedDevice?.id) {
      alert('Select a TV/device first.');
      return;
    }
    const debugDefaultAds = localStorage.getItem('debugDefaultAds') === '1';
    if (settingsDebounceRef.current) {
      window.clearTimeout(settingsDebounceRef.current);
      settingsDebounceRef.current = null;
    }
    const ok = await saveOverlaySettings({ silent: false });
    if (!ok) return;
    try {
      if (debugDefaultAds) {
        console.log('[DefaultAdsUI] Preview on TV request', {
          deviceId: selectedDevice.id,
          overlayId: selectedFullOverlay.id,
        });
      }
      await axios.post(
        '/api/overlays/play',
        {
          deviceId: selectedDevice.id,
          overlayId: selectedFullOverlay.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (err) {
      console.error('Failed to preview default ad overlay', err);
      alert('Failed to preview on TV.');
    }
  };

  const handleSave = async () => {
    if (!siteId) return;
    setSaving(true);
    setError('');
    try {
      await axios.put(
        `/api/sites/${siteId}/wifi`,
        {
          wifi_ssid: wifiSsid,
          wifi_password: wifiPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInitialWifi({ ssid: wifiSsid, password: wifiPassword });
      return true;
    } catch (err) {
      console.error('Error saving wifi settings', err);
      setError('Failed to save wifi settings.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const injectPrintStyle = (orientation) => {
    const styleId = 'wifi-print-style';
    document.getElementById(styleId)?.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
@page { size: A4 ${orientation}; margin: 0; }
@media print {
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body * { visibility: hidden !important; }
  #wifi-print-root, #wifi-print-root * { visibility: visible !important; }
  #wifi-print-root {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center;
  }
  #wifi-print-root .wifi-poster--landscape {
    width: 297mm !important;
    max-width: none !important;
  }
  #wifi-print-root .wifi-poster--portrait {
    width: 210mm !important;
    max-width: none !important;
  }
}`;
    document.head.appendChild(style);

    const cleanup = () => {
      style.remove();
    };

    return cleanup;
  };

  const doPrint = () => {
    if (!enabled) return;
    setPrintOpen(false);
    window.setTimeout(() => {
      printCleanupRef.current?.();
      printCleanupRef.current = injectPrintStyle(selectedTemplate.orientation);
      const handler = () => {
        printCleanupRef.current?.();
        printCleanupRef.current = () => {};
        window.removeEventListener('afterprint', handler);
      };
      window.addEventListener('afterprint', handler);
      window.print();
    }, 50);
  };

  const saveAndPrint = async () => {
    if (selectedAd.type !== 'wifi') return;
    const ok = await handleSave();
    if (!ok) return;
    doPrint();
  };

  if (!siteId) {
    return (
      <div className="wifi-tab">
        <p className="wifi-tab-muted">Select a site to view WiFi details.</p>
      </div>
    );
  }

  const Poster = selectedTemplate.Component;

  if (urlMissing) {
    return (
      <div className="wifi-tab">
        <div className="wifi-tab-subtabs">
          {defaultAds.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`wifi-tab-subtab ${tab.id === selectedAdId ? 'wifi-tab-subtab--active' : ''}`}
              onClick={() => setSelectedAdId(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="wifi-tab-empty">
          <div className="wifi-tab-emptyTitle">{selectedAd.label}</div>
          <div className="wifi-tab-emptyBody">
            {missingUrlMessage}{' '}
            <button type="button" className="wifi-tab-link" onClick={() => onGoToSiteDetails?.()}>
              Open Settings / Site Details
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wifi-tab">
      <div className="wifi-tab-subtabs">
        {defaultAds.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`wifi-tab-subtab ${tab.id === selectedAdId ? 'wifi-tab-subtab--active' : ''}`}
            onClick={() => setSelectedAdId(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="wifi-tab-templateBar">
        <div className="wifi-tab-templateBarHeader">
          <div>
            <div className="wifi-tab-subtitle">Templates</div>
            <div className="wifi-tab-muted wifi-tab-templateHintRow">
              <span>Pick a design, then print</span>
              <Badge className="bg-white/10 ring-white/20 text-slate-100">
                A4 {selectedTemplate.orientation === 'landscape' ? 'Landscape' : 'Portrait'}
              </Badge>
            </div>
          </div>
          <button
            type="button"
            className="wifi-tab-previewTv"
            onClick={previewOnTv}
            disabled={!selectedDevice?.id || savingOverlaySettings}
            title={!selectedDevice?.id ? 'Select a device first' : 'Preview this default ad on the selected device'}
          >
            {savingOverlaySettings ? 'Saving…' : 'Preview on selected TV'}
          </button>
        </div>
        <div className="wifi-tab-templates">
          {WIFI_POSTER_TEMPLATES.map((tpl) => (
            <button
              type="button"
              key={tpl.id}
              className={`wifi-tab-templateCard ${
                tpl.id === selectedTemplateId ? 'wifi-tab-templateCard--active' : ''
              }`}
              onClick={() => setSelectedTemplateId(tpl.id)}
            >
              <div className="wifi-tab-templateEmoji" aria-hidden="true">{tpl.emoji}</div>
              <div className="wifi-tab-templateMeta">
                <div className="wifi-tab-templateName">{tpl.name}</div>
                <div className="wifi-tab-templateDesc">{tpl.description}</div>
                <div className="wifi-tab-templateFooter">
                  <span className="wifi-tab-templateTag">
                    {tpl.orientation === 'landscape' ? 'Landscape' : 'Portrait'}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="wifi-tab-previewCard">
        <div className="wifi-tab-previewHeader">
          <div className="wifi-tab-previewTitle">
            <div className="wifi-tab-previewTitleRow">
              <h3 className="wifi-tab-subtitle">Preview</h3>
              <Badge className="bg-white/10 ring-white/20 text-slate-100">
                {selectedTemplate.orientation === 'landscape' ? 'A4 Landscape' : 'A4 Portrait'}
              </Badge>
            </div>
            <div className="wifi-tab-muted">{selectedTemplate.name}</div>
          </div>
          <div className="wifi-tab-previewActions">
            <button
              type="button"
              className="wifi-tab-print"
              onClick={() => setPrintOpen(true)}
              disabled={!enabled}
              title={!enabled ? 'Add the required URL in Site Details to enable printing' : 'Print poster'}
            >
              Print
            </button>
          </div>
        </div>

        <div className="wifi-tab-previewStage">
          <div id="wifi-print-root" className="wifi-print-root">
            <div className={`wifi-poster wifi-poster--${selectedTemplate.orientation} wifi-poster--${selectedTemplate.id}`}>
              <Poster
                venue={venue}
                qrDataUrl={qrDataUrl}
                copy={effectiveCopy}
                details={details}
                steps={selectedAd.steps}
                visibleFields={visibleFields}
                defaultKey={selectedAd.defaultKey}
              />
            </div>
          </div>
        </div>

        {!enabled ? (
          <div className="wifi-tab-help">
            {selectedAd.type === 'wifi'
              ? 'Enter an SSID to generate the QR code and enable printing.'
              : 'Add the required URL in Settings / Site Details to enable printing.'}
          </div>
        ) : (
          <div className="wifi-tab-help">Tip: print on matte paper and place near the bar/entrance.</div>
        )}
      </div>

      <div className="wifi-tab-infoBar">
        <div className="wifi-tab-infoBarLeft">
          <div className="wifi-tab-infoTitle">{selectedAd.label}</div>
          {selectedAd.type === 'wifi' ? (
            <div className="wifi-tab-infoWifi">
              <div className="wifi-tab-field">
                <label>SSID</label>
                <input
                  type="text"
                  value={wifiSsid}
                  onChange={(e) => setWifiSsid(e.target.value)}
                  placeholder={loading ? 'Loading…' : 'Network name'}
                  disabled={loading || saving}
                />
              </div>
              <div className="wifi-tab-field">
                <label>Password</label>
                <div className="wifi-tab-password">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={wifiPassword}
                    onChange={(e) => setWifiPassword(e.target.value)}
                    placeholder={loading ? 'Loading…' : 'Network password'}
                    disabled={loading || saving}
                  />
                  <button
                    type="button"
                    className="wifi-tab-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    disabled={loading || saving}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              <div className="wifi-tab-actions">
                <button
                  type="button"
                  className="wifi-tab-save"
                  onClick={handleSave}
                  disabled={loading || saving}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <Badge className={dirty ? 'bg-amber-500/20 ring-amber-400/30 text-amber-200' : 'bg-emerald-500/20 ring-emerald-300/30 text-emerald-100'}>
                  {dirty ? 'Unsaved changes' : 'Saved'}
                </Badge>
              </div>
            </div>
          ) : (
            <div className="wifi-tab-infoUrl">
              <div className="wifi-tab-infoChip">
                <div className="wifi-tab-infoChipLabel">{selectedAd.requiredLabel}</div>
                <div className="wifi-tab-infoChipValue">{venue[selectedAd.field] ? shortUrl(venue[selectedAd.field]) : '—'}</div>
              </div>
              <Badge className={enabled ? 'bg-emerald-500/20 ring-emerald-300/30 text-emerald-100' : 'bg-slate-500/20 ring-white/10 text-slate-200'}>
                {enabled ? 'Enabled' : 'Needs URL'}
              </Badge>
            </div>
          )}

          <div className="wifi-tab-copyEditor">
            <div className="wifi-tab-copyTitle">Template text</div>
            <div className="wifi-tab-copyGrid">
              <div className="wifi-tab-field">
                <label>Kicker</label>
                <input
                  type="text"
                  value={templateCopy.kicker}
                  onChange={(e) => setTemplateCopy((prev) => ({ ...prev, kicker: e.target.value }))}
                  placeholder={selectedAd.copy?.kicker || ''}
                />
              </div>
              <div className="wifi-tab-field">
                <label>Title</label>
                <input
                  type="text"
                  value={templateCopy.title}
                  onChange={(e) => setTemplateCopy((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder={selectedAd.copy?.title || ''}
                />
              </div>
              <div className="wifi-tab-field">
                <label>Sub</label>
                <input
                  type="text"
                  value={templateCopy.sub}
                  onChange={(e) => setTemplateCopy((prev) => ({ ...prev, sub: e.target.value }))}
                  placeholder={selectedAd.copy?.sub || ''}
                />
              </div>
            </div>
            <div className="wifi-tab-muted">
              Changes save automatically and apply next time the Default Ad plays.
            </div>
          </div>

          {error && <div className="wifi-tab-error">{error}</div>}
        </div>

        <div className="wifi-tab-infoBarRight">
          <div className="wifi-tab-infoChip wifi-tab-visibilityBox">
            <div className="wifi-tab-infoChipLabel">Pulled from site details</div>

            <label className="wifi-tab-visibilityRow">
              <input
                type="checkbox"
                checked={visibleFields.venue}
                onChange={(e) => setVisibleFields((prev) => ({ ...prev, venue: e.target.checked }))}
              />
              <span className="wifi-tab-visibilityLabel">Venue</span>
              <span className="wifi-tab-visibilityValue">{venue.name || '—'}</span>
            </label>

            <label className="wifi-tab-visibilityRow">
              <input
                type="checkbox"
                checked={visibleFields.address}
                onChange={(e) => setVisibleFields((prev) => ({ ...prev, address: e.target.checked }))}
              />
              <span className="wifi-tab-visibilityLabel">Address</span>
              <span className="wifi-tab-visibilityValue">{venue.address || '—'}</span>
            </label>

            <label className="wifi-tab-visibilityRow">
              <input
                type="checkbox"
                checked={visibleFields.website}
                onChange={(e) => setVisibleFields((prev) => ({ ...prev, website: e.target.checked }))}
              />
              <span className="wifi-tab-visibilityLabel">Website</span>
              <span className="wifi-tab-visibilityValue">{venue.website ? shortUrl(venue.website) : '—'}</span>
            </label>

            <label className="wifi-tab-visibilityRow">
              <input
                type="checkbox"
                checked={visibleFields.link}
                onChange={(e) => setVisibleFields((prev) => ({ ...prev, link: e.target.checked }))}
              />
              <span className="wifi-tab-visibilityLabel">Link</span>
              <span className="wifi-tab-visibilityValue">
                {selectedAd.type === 'wifi'
                  ? wifiSsid.trim() || '—'
                  : (venue[selectedAd.field] ? shortUrl(venue[selectedAd.field]) : '—')}
              </span>
            </label>

            <label className="wifi-tab-visibilityRow">
              <input
                type="checkbox"
                checked={visibleFields.details}
                onChange={(e) => setVisibleFields((prev) => ({ ...prev, details: e.target.checked }))}
              />
              <span className="wifi-tab-visibilityLabel">Details block</span>
              <span className="wifi-tab-visibilityValue">
                {selectedAd.type === 'wifi' ? 'Network + password' : 'Action + URL'}
              </span>
            </label>
          </div>
        </div>
      </div>

      <Modal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        title={`Print: ${selectedAd.label}`}
      >
        <div className="wifi-tab-printDialog">
          <div className="wifi-tab-printSummary">
            <div className="wifi-tab-printLine">
              <span className="wifi-tab-printLabel">Template</span>
              <span className="wifi-tab-printValue">{selectedTemplate.name}</span>
            </div>
            <div className="wifi-tab-printLine">
              <span className="wifi-tab-printLabel">Paper</span>
              <span className="wifi-tab-printValue">
                A4 {selectedTemplate.orientation === 'landscape' ? 'Landscape' : 'Portrait'}
              </span>
            </div>
            {selectedAd.type === 'wifi' && dirty ? (
              <div className="wifi-tab-printWarn">
                You have unsaved changes. The printout uses the current preview.
              </div>
            ) : null}
          </div>

          <div className="wifi-tab-printNotes">
            <div className="wifi-tab-printNotesTitle">Recommended print settings</div>
            <ul className="wifi-tab-printNotesList">
              <li>Scale: 100% (disable “Fit to page” if it adds margins).</li>
              <li>Background graphics: On.</li>
              <li>Orientation: {selectedTemplate.orientation === 'landscape' ? 'Landscape' : 'Portrait'}.</li>
            </ul>
          </div>

          <div className="wifi-tab-printButtons">
            <button type="button" className="wifi-tab-printSecondary" onClick={() => setPrintOpen(false)}>
              Cancel
            </button>
            <button type="button" className="wifi-tab-printSecondary" onClick={doPrint} disabled={!enabled}>
              Print
            </button>
            {selectedAd.type === 'wifi' ? (
              <button type="button" className="wifi-tab-printPrimary" onClick={saveAndPrint} disabled={!enabled || saving || loading}>
                Save &amp; Print
              </button>
            ) : null}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default WifiTab;
